
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Settings, AlertTriangle } from 'lucide-react';
import { Client } from '@/models/Client';
import { useTranslation } from 'react-i18next';
import { useWorkoutTypes } from '@/hooks/useWorkoutTypes';
import ClientSessionsModal from './ClientSessionsModal';

interface ClientSessionsSectionProps {
  client: Client;
}

const ClientSessionsSection: React.FC<ClientSessionsSectionProps> = ({ client }) => {
  const { t } = useTranslation();
  const [showSessionsModal, setShowSessionsModal] = useState(false);
  const { workoutTypes = [] } = useWorkoutTypes();

  const getWorkoutTypeName = (workoutTypeId: string) => {
    const workoutType = workoutTypes.find(wt => wt.id === workoutTypeId);
    return workoutType?.name || 'Unknown';
  };

  const getWorkoutTypeColor = (workoutTypeId: string) => {
    const workoutType = workoutTypes.find(wt => wt.id === workoutTypeId);
    return workoutType?.color || '#3498db';
  };

  const activeSubscriptions = client.subscriptions.filter(sub => sub.isActive);
  const totalRemainingSessions = activeSubscriptions.reduce((total, sub) => 
    total + (sub.sessionsPerMonth - sub.currentCycleSessionsUsed), 0
  );

  return (
    <>
      <Card>
        <CardHeader className="pb-3 md:pb-6">
          <CardTitle className="flex items-center gap-2 text-base md:text-lg">
            <Calendar className="h-4 w-4 md:h-5 md:w-5" />
            {t('clients.sessions')}
          </CardTitle>
        </CardHeader>
        
        <CardContent className="space-y-3 md:space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-xs md:text-sm text-muted-foreground">{t('clients.totalRemaining')}:</span>
            <div className="flex items-center gap-2">
              <span className="font-semibold">{totalRemainingSessions}</span>
              {totalRemainingSessions <= 2 && (
                <AlertTriangle className="h-4 w-4 text-orange-500" />
              )}
            </div>
          </div>
          
          {activeSubscriptions.length > 0 ? (
            <div className="space-y-2 md:space-y-3">
              {activeSubscriptions.map((subscription) => {
                const remaining = subscription.sessionsPerMonth - subscription.currentCycleSessionsUsed;
                const isLow = remaining <= 1;
                
                return (
                  <div
                    key={subscription.id}
                    className="flex items-center justify-between p-2 md:p-3 rounded-lg border"
                  >
                    <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
                      <Badge
                        variant="secondary"
                        className="text-xs"
                        style={{
                          backgroundColor: `${getWorkoutTypeColor(subscription.workoutTypeId)}20`,
                          color: getWorkoutTypeColor(subscription.workoutTypeId),
                          borderColor: getWorkoutTypeColor(subscription.workoutTypeId),
                        }}
                      >
                        {getWorkoutTypeName(subscription.workoutTypeId)}
                      </Badge>
                      {isLow && <AlertTriangle className="h-3 w-3 md:h-4 md:w-4 text-orange-500 flex-shrink-0" />}
                    </div>
                    
                    <div className="text-right">
                      <div className="font-medium text-xs md:text-sm">
                        {subscription.currentCycleSessionsUsed}/{subscription.sessionsPerMonth}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {remaining} {t('clients.remaining')}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center text-muted-foreground py-4 text-xs md:text-sm">
              {t('clients.noActiveSubscriptions')}
            </div>
          )}
          
          <Button 
            onClick={() => setShowSessionsModal(true)}
            variant="outline"
            className="w-full text-xs md:text-sm"
            size="sm"
          >
            <Settings className="h-3 w-3 md:h-4 md:w-4 mr-2" />
            {t('clients.manageSessions')}
          </Button>
        </CardContent>
      </Card>

      <ClientSessionsModal
        client={client}
        open={showSessionsModal}
        onOpenChange={setShowSessionsModal}
      />
    </>
  );
};

export default ClientSessionsSection;
